import React, { useState, useEffect, useCallback } from 'react';
import { supabase, deleteUser, buildDeletionAuditHtml, type DeletionAuditData } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Clock, ChevronDown, ChevronUp, X, Trash2, Search, UserCircle2, Shield, CreditCard, ChevronRight, Check, Loader2, ChevronLeft, Phone, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { formatDateColombia } from '../utils/dateFormat';
import * as XLSX from 'xlsx';

interface User {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  role: string;
  credits: number;
  credits_promotional: number;
  credits_real: number;
  created_at: string;
}

interface AuditLogRaw {
  id: string;
  user_id: string;
  modified_by: string;
  field_changed: string;
  old_value: string;
  new_value: string;
  created_at: string;
  profiles: {
    first_name: string;
    last_name: string;
  };
}

interface AuditLog {
  id: string;
  user_id: string;
  modified_by: string;
  field_changed: string;
  old_value: string;
  new_value: string;
  created_at: string;
  modifier_first_name: string;
  modifier_last_name: string;
}

interface UserWithEmail {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  role: string;
  credits: number;
  credits_promotional: number;
  credits_real: number;
  created_at: string;
}

// Cache para usuarios y logs de auditoría
let usersCache: User[] | null = null;
let auditLogsCache: { [key: string]: AuditLog[] } = {};
let lastUsersFetchTime: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

export function clearAdminUsersCache() {
  usersCache = null;
  auditLogsCache = {};
  lastUsersFetchTime = 0;
}

function formatPhoneForDisplay(phone: string): string {
  const digits = (phone || '').replace(/\D/g, '');
  if (!digits) return '-';

  // Si viene con longitud internacional, asumimos últimos 10 dígitos como número local.
  if (digits.length > 10) {
    const countryCode = digits.slice(0, digits.length - 10);
    const local = digits.slice(-10);
    return `+${countryCode} ${local.slice(0, 3)} ${local.slice(3, 6)} ${local.slice(6)}`;
  }

  // Formato local común de 10 dígitos.
  if (digits.length === 10) {
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  }

  // Fallback para longitudes no estándar.
  return phone;
}

export default function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [creditIncrements, setCreditIncrements] = useState<{ [key: string]: number }>({});
  const [creditChargeTypes, setCreditChargeTypes] = useState<{ [key: string]: 'promocional' | 'venta' }>({});
  const [updatingUser, setUpdatingUser] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortColumn, setSortColumn] = useState<'user' | 'email' | 'created_at'>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

  const handleSort = (column: 'user' | 'email' | 'created_at') => {
    if (sortColumn === column) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortOrder(column === 'created_at' ? 'desc' : 'asc');
    }
  };

  const SortIcon = ({ column }: { column: 'user' | 'email' | 'created_at' }) =>
    sortColumn === column ? (
      sortOrder === 'desc' ? <ChevronDown className="w-4 h-4 text-blue-400 shrink-0" /> : <ChevronUp className="w-4 h-4 text-blue-400 shrink-0" />
    ) : (
      <ChevronDown className="w-4 h-4 text-gray-500 opacity-40 shrink-0" />
    );
  const { user: currentUser, profile: currentProfile } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    fetchUsers();
  }, []);

  // Limpiar estados cuando no hay usuario autenticado
  useEffect(() => {
    if (!currentUser) {
      setUsers([]);
      setLoading(false);
      setAuditLogs([]);
      setSelectedUserId(null);
      setShowAuditModal(false);
      setCreditIncrements({});
      setCreditChargeTypes({});
      setUpdatingUser(null);
      setSearchTerm('');
      setShowConfirmDialog(false);
      setUserToDelete(null);
      setCurrentPage(1);
      setSortColumn('created_at');
      setSortOrder('desc');
    }
  }, [currentUser]);

  useEffect(() => {
    if (selectedUserId && showAuditModal) {
      fetchAuditLogs(selectedUserId, true).catch(() => {});
    }
  }, [selectedUserId, showAuditModal]);

  const exportUsersToXlsx = useCallback(() => {
    try {
      if (!users.length) {
        toast.error('No hay usuarios para exportar');
        return;
      }

      const rows = users.map((user) => ({
        ID: user.id,
        Nombres: user.first_name || '',
        Apellidos: user.last_name || '',
        Email: user.email || '',
        Telefono: formatPhoneForDisplay(user.phone),
        TelefonoRaw: user.phone || '',
        Rol: user.role || 'user',
        Creditos: user.credits || 0,
        CreditosPromocionales: user.credits_promotional || 0,
        CreditosReales: user.credits_real || 0,
        FechaCreacion: formatDateColombia(user.created_at),
        FechaCreacionISO: user.created_at || ''
      }));

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Usuarios');

      const now = new Date();
      const filename = `usuarios_${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}.xlsx`;
      XLSX.writeFile(workbook, filename);

      toast.success(`Exportación completada: ${users.length} usuarios`);
    } catch (error) {
      console.error('Error exporting users to xlsx:', error);
      toast.error('Error al exportar usuarios');
    }
  }, [users]);

  const fetchUsers = async (forceRefresh = false) => {
    try {
      // Verificar si podemos usar el caché
      const now = Date.now();
      if (!forceRefresh && usersCache && (now - lastUsersFetchTime) < CACHE_DURATION) {
        setUsers(usersCache);
        setLoading(false);
        return;
      }

      setLoading(true);
      const { data: profiles, error: profilesError } = await supabase()
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (profilesError) {
        console.error('Error fetching profiles:', profilesError);
        throw profilesError;
      }

      const formattedUsers = profiles.map(profile => ({
        id: profile.id as string,
        email: profile.email as string || '',
        first_name: profile.first_name as string || '',
        last_name: profile.last_name as string || '',
        phone: profile.phone as string || '',
        role: profile.role as string || 'user',
        credits: Number(profile.credits) || 0,
        credits_promotional: Number((profile as any).credits_promotional) || 0,
        credits_real: Number((profile as any).credits_real) || 0,
        created_at: profile.created_at as string
      } as User));

      setUsers(formattedUsers);
      // Actualizar caché
      usersCache = formattedUsers;
      lastUsersFetchTime = now;
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Error al cargar los usuarios');
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLogs = async (userId: string, forceRefresh = false) => {
    try {
      if (!forceRefresh && auditLogsCache[userId]) {
        setAuditLogs(auditLogsCache[userId]);
        return;
      }

      const { data: logs, error } = await supabase()
        .from('user_audit_log')
        .select(`
          id,
          user_id,
          modified_by,
          field_changed,
          old_value,
          new_value,
          created_at,
          profiles!modified_by (
            first_name,
            last_name
          )
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(500);

      if (error) throw error;

      const formattedLogs: AuditLog[] = (logs as unknown as AuditLogRaw[]).map(log => ({
        ...log,
        modifier_first_name: log.profiles?.first_name || '',
        modifier_last_name: log.profiles?.last_name || '',
      }));

      setAuditLogs(formattedLogs);
      auditLogsCache[userId] = formattedLogs;
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      toast.error('Error al cargar el historial de cambios');
      setAuditLogs([]);
    }
  };

  /** Obtiene logs de auditoría sin caché (para envío en correo de eliminación) */
  const fetchAuditLogsForDeletion = async (userId: string): Promise<AuditLog[]> => {
    const { data: logs, error } = await supabase()
      .from('user_audit_log')
      .select(`
        id,
        user_id,
        modified_by,
        field_changed,
        old_value,
        new_value,
        created_at,
        profiles!modified_by (
          first_name,
          last_name
        )
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return (logs as unknown as AuditLogRaw[]).map(log => ({
      ...log,
      modifier_first_name: log.profiles?.first_name || '',
      modifier_last_name: log.profiles?.last_name || '',
    }));
  };

  const logAuditChange = async (userId: string, field: string, oldValue: string, newValue: string) => {
    try {
      if (!currentUser) throw new Error('No user authenticated');

      const auditEntry = {
        user_id: userId,
        modified_by: currentUser.id,
        field_changed: field,
        old_value: oldValue,
        new_value: newValue,
        created_at: new Date().toISOString()
      };

      console.log('Creating audit log entry:', auditEntry);

      const { error } = await supabase()
        .from('user_audit_log')
        .insert(auditEntry);

      if (error) {
        console.error('Error in logAuditChange:', error);
        throw error;
      }

      // Actualizar el historial inmediatamente después de registrar un cambio
      if (userId === selectedUserId) {
        fetchAuditLogs(userId);
      }
    } catch (error) {
      console.error('Error logging audit:', error);
      toast.error('Error al registrar el cambio en el historial');
    }
  };

  const handleRoleChange = async (userId: string, newRole: string) => {
    try {
      setUpdatingUser(userId);
      const user = users.find(u => u.id === userId);
      if (!user) {
        toast.error('Usuario no encontrado');
        return;
      }

      // Validar que el rol sea uno de los permitidos
      const validRoles = ['user', 'admin', 'super_admin'];
      if (!validRoles.includes(newRole)) {
        toast.error('Rol no válido');
        return;
      }

      // Primero registrar en la auditoría
      await logAuditChange(
        userId,
        'role',
        user.role || 'user',
        newRole
      );

      // Luego actualizar el rol
      const { error } = await supabase()
        .from('profiles')
        .update({ role: newRole })
        .eq('id', userId);

      if (error) throw error;

      toast.success('Rol actualizado correctamente');
      
      // Actualizar la lista de usuarios y el historial
      await fetchUsers(true); // Forzar actualización
      if (selectedUserId === userId) {
        await fetchAuditLogs(userId);
      }
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error('Error al actualizar el rol');
    } finally {
      setUpdatingUser(null);
    }
  };

  const handleCreditsChange = async (userId: string, increment: number, chargeType?: 'promocional' | 'venta') => {
    try {
      setUpdatingUser(userId);
      const user = users.find(u => u.id === userId);
      if (!user) {
        toast.error('Usuario no encontrado');
        return;
      }

      // Si el incremento es 0, no hacer nada
      if (increment === 0) {
        // Limpiar el input de incremento
        setCreditIncrements(prev => {
          const newState = { ...prev };
          delete newState[userId];
          return newState;
        });
        setCreditChargeTypes(prev => {
          const newState = { ...prev };
          delete newState[userId];
          return newState;
        });
        return;
      }

      const currentPromotional = Number(user.credits_promotional) || 0;
      const currentReal = Number(user.credits_real) || 0;
      let newPromotional = currentPromotional;
      let newReal = currentReal;

      if (increment > 0) {
        // Recargas separadas por tipo para contabilidad real
        if (chargeType === 'promocional') {
          newPromotional += increment;
        } else {
          newReal += increment;
        }
      } else {
        // Retiros administrativos siguen la política promo_first
        let remaining = Math.abs(increment);
        const fromPromo = Math.min(newPromotional, remaining);
        newPromotional -= fromPromo;
        remaining -= fromPromo;
        const fromReal = Math.min(newReal, remaining);
        newReal -= fromReal;
        remaining -= fromReal;
        if (remaining > 0) {
          toast.error('No hay saldo suficiente para aplicar el retiro');
          return;
        }
      }

      // Total contable como suma de bolsas
      const newCredits = newPromotional + newReal;

      // Validar que los créditos resultantes no sean negativos
      if (newCredits < 0) {
        toast.error('Los créditos resultantes no pueden ser negativos');
        // Limpiar el input de incremento
        setCreditIncrements(prev => {
          const newState = { ...prev };
          delete newState[userId];
          return newState;
        });
        setCreditChargeTypes(prev => {
          const newState = { ...prev };
          delete newState[userId];
          return newState;
        });
        return;
      }

      // Primero registrar en la auditoría (incluir tipo de carga si aplica)
      const auditNewValue = increment > 0 && chargeType
        ? `${newCredits} (${chargeType})`
        : newCredits.toString();
      await logAuditChange(
        userId,
        'credits',
        user.credits.toString(),
        auditNewValue
      );

      // Luego actualizar los créditos
      const { error: profileError } = await supabase()
        .from('profiles')
        .update({
          credits: newCredits,
          credits_promotional: newPromotional,
          credits_real: newReal,
        })
        .eq('id', userId);

      if (profileError) throw profileError;

      // Registrar transacción en credit_transactions vía RPC (bypasea RLS)
      const transactionDescription = increment > 0
        ? (chargeType === 'venta'
          ? 'Recarga de créditos por venta'
          : 'Recarga de créditos promocional')
        : 'Retiro de créditos por administrador';

      const { data: rpcData, error: transactionError } = await supabase().rpc('insert_credit_transaction', {
        p_user_id: userId,
        p_amount: increment,
        p_description: transactionDescription,
        p_charge_type: increment > 0 ? chargeType ?? null : null
      });

      if (transactionError || (rpcData && !rpcData.ok)) {
        console.error('Error al registrar transacción:', transactionError || rpcData?.error);
        // No lanzar error, solo loguear
      }

      const incrementMessage = increment > 0 
        ? `Se agregaron ${increment} créditos. Total: ${newCredits}`
        : `Se retiraron ${Math.abs(increment)} créditos. Total: ${newCredits}`;
      toast.success(incrementMessage);
      
      // Limpiar el input de incremento y tipo de carga
      setCreditIncrements(prev => {
        const newState = { ...prev };
        delete newState[userId];
        return newState;
      });
      setCreditChargeTypes(prev => {
        const newState = { ...prev };
        delete newState[userId];
        return newState;
      });

      // Actualizar la lista de usuarios y el historial
      await fetchUsers(true); // Forzar actualización
      if (selectedUserId === userId) {
        await fetchAuditLogs(userId);
      }
    } catch (error) {
      console.error('Error updating credits:', error);
      toast.error('Error al actualizar los créditos');
    } finally {
      setUpdatingUser(null);
    }
  };

  const handleCreditsInputChange = (userId: string, value: string) => {
    // Permitir que el usuario escriba el signo negativo primero
    if (value === '' || value === '-') {
      setCreditIncrements(prev => {
        const newState = { ...prev };
        delete newState[userId];
        return newState;
      });
      setCreditChargeTypes(prev => {
        const newState = { ...prev };
        delete newState[userId];
        return newState;
      });
      return;
    }

    // Interpretar el valor como incremento (puede ser positivo o negativo)
    const numericValue = parseInt(value, 10);

    // Si no es un número válido, no actualizar (permitir que el usuario siga escribiendo)
    if (isNaN(numericValue)) {
      return;
    }

    setCreditIncrements(prev => ({
      ...prev,
      [userId]: numericValue
    }));

    // Si es recarga positiva, establecer tipo por defecto si no existe
    if (numericValue > 0) {
      setCreditChargeTypes(prev => ({
        ...prev,
        [userId]: prev[userId] ?? 'promocional'
      }));
    } else {
      setCreditChargeTypes(prev => {
        const newState = { ...prev };
        delete newState[userId];
        return newState;
      });
    }
  };

  const handleCreditsBlur = (userId: string) => {
    // No guardar automáticamente al perder el foco
    // El usuario debe hacer clic en el botón de guardar
  };

  const handleSaveCredits = async (userId: string) => {
    const increment = creditIncrements[userId];
    // Solo procesar si hay un incremento válido y diferente de 0
    if (increment !== undefined && increment !== 0) {
      const chargeType = increment > 0 ? creditChargeTypes[userId] : undefined;
      await handleCreditsChange(userId, increment, chargeType);
    }
  };

  const handleCreditsKeyPress = (e: React.KeyboardEvent<HTMLInputElement>, userId: string) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
      // Guardar al presionar Enter si hay un cambio pendiente
      const increment = creditIncrements[userId];
      if (increment !== undefined && increment !== 0) {
        handleSaveCredits(userId);
      }
    }
  };

  const handleDeleteClick = (user: User) => {
    setUserToDelete(user);
    setShowConfirmDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete || !currentUser) return;

    try {
      setLoading(true);

      // 1. Registrar eliminación en auditoría (antes de borrar, para que quede en los logs)
      try {
        await logAuditChange(userToDelete.id, 'account_deleted', 'active', 'deleted');
      } catch {
        // Si falla el log (ej. permisos), continuamos; los logs existentes se incluirán
      }

      // 2. Recopilar datos de auditoría antes de la eliminación (los logs se borran con CASCADE)
      const logs = await fetchAuditLogsForDeletion(userToDelete.id);
      const auditData: DeletionAuditData = {
        deletedBy: {
          name: currentProfile ? `${currentProfile.first_name} ${currentProfile.last_name}`.trim() || currentProfile.email : 'Administrador',
          email: currentProfile?.email ?? currentUser.email ?? ''
        },
        deletedAt: new Date().toISOString(),
        userDeleted: {
          firstName: userToDelete.first_name,
          lastName: userToDelete.last_name,
          email: userToDelete.email,
          role: userToDelete.role,
          credits: userToDelete.credits ?? 0
        },
        auditLogs: logs.map((l) => ({
          field_changed: l.field_changed,
          old_value: l.old_value ?? '',
          new_value: l.new_value ?? '',
          created_at: l.created_at,
          modifier_name: `${l.modifier_first_name} ${l.modifier_last_name}`.trim() || 'Sistema'
        })),
        auditHtml: ''
      };
      auditData.auditHtml = buildDeletionAuditHtml(auditData);

      const result = await deleteUser(userToDelete.id, userToDelete.email, auditData);

      if (result.success) {
        const successMessage = typeof result.message === 'string'
          ? result.message
          : 'Usuario eliminado correctamente';
        toast.success(successMessage);
        await fetchUsers(true);
        if (userToDelete.id) {
          delete auditLogsCache[userToDelete.id];
        }
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Error al eliminar usuario:', error);
      toast.error(error instanceof Error ? error.message : 'Error al eliminar usuario');
    } finally {
      setShowConfirmDialog(false);
      setUserToDelete(null);
      setLoading(false);
    }
  };

  const filteredUsers = users
    .filter(user => 
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.phone.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.last_name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      let cmp = 0;
      if (sortColumn === 'user') {
        const nameA = `${(a.first_name || '').toLowerCase()} ${(a.last_name || '').toLowerCase()}`.trim();
        const nameB = `${(b.first_name || '').toLowerCase()} ${(b.last_name || '').toLowerCase()}`.trim();
        cmp = nameA.localeCompare(nameB);
      } else if (sortColumn === 'email') {
        cmp = (a.email || '').toLowerCase().localeCompare((b.email || '').toLowerCase());
      } else {
        cmp = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      }
      return sortOrder === 'asc' ? cmp : -cmp;
    });

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedUsers = filteredUsers.slice(startIndex, startIndex + pageSize);

  // Reset a página 1 cuando cambia el filtro o el orden
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, pageSize, sortColumn, sortOrder]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1B1E27] text-white">
      {/* Header */}
      <div className="bg-[#1F2937] border-b border-gray-700 p-4 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Volver
            </button>
            <h1 className="text-2xl font-bold">Administración de Usuarios</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={exportUsersToXlsx}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600/90 hover:bg-blue-600 text-white rounded-lg border border-blue-400/30 shadow-sm transition-colors"
              title="Exportar todos los usuarios a XLSX"
            >
              <Download className="w-4 h-4" />
              <span className="text-sm font-medium">Exportar XLSX</span>
            </button>
            <div className="relative w-96">
              <input
                type="text"
                placeholder="Buscar usuarios..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 bg-[#111827] text-white rounded-lg pl-10 focus:outline-none focus:ring-2 focus:ring-blue-500 border border-gray-700"
              />
              <Search className="absolute left-3 top-2.5 text-gray-400 w-5 h-5" />
            </div>
          </div>
        </div>
      </div>

      {/* Contenido principal */}
      <div className="max-w-7xl mx-auto p-6">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <div className="bg-[#1F2937] rounded-xl shadow-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-700">
                <thead className="bg-[#111827]">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      <button
                        type="button"
                        onClick={() => handleSort('user')}
                        className="flex items-center gap-2 hover:text-white transition-colors group"
                        title="Ordenar por usuario"
                      >
                        <UserCircle2 className="w-4 h-4" />
                        <span>Usuario</span>
                        <SortIcon column="user" />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      <button
                        type="button"
                        onClick={() => handleSort('email')}
                        className="flex items-center gap-2 hover:text-white transition-colors group"
                        title="Ordenar por correo"
                      >
                        <span>Email</span>
                        <SortIcon column="email" />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      <div className="flex items-center space-x-2">
                        <Shield className="w-4 h-4" />
                        <span>Rol</span>
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider w-80">
                      <div className="flex items-center space-x-2">
                        <CreditCard className="w-4 h-4" />
                        <span>Créditos</span>
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      <button
                        type="button"
                        onClick={() => handleSort('created_at')}
                        className="flex items-center gap-2 hover:text-white transition-colors group"
                        title="Ordenar por fecha de creación"
                      >
                        <span>Fecha de creación</span>
                        <SortIcon column="created_at" />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      <div className="flex items-center space-x-2">
                        <Phone className="w-4 h-4" />
                        <span>Teléfono</span>
                      </div>
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {paginatedUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-[#2D3748] transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 flex-shrink-0">
                            <div className="h-10 w-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center">
                              <span className="text-lg font-medium text-white">
                                {user.first_name[0]}{user.last_name[0]}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-white">
                              {user.first_name} {user.last_name}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-300">{user.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={user.role}
                          onChange={(e) => handleRoleChange(user.id, e.target.value)}
                          disabled={updatingUser === user.id}
                          className="text-sm bg-[#111827] border border-gray-600 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                        >
                          <option value="user">Usuario</option>
                          <option value="admin">Administrador</option>
                          <option value="super_admin">Super Admin</option>
                        </select>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-white tabular-nums w-12">{user.credits}</span>
                          <div className="inline-flex items-center gap-1.5 bg-[#111827]/80 rounded-lg border border-gray-700 px-2 py-1">
                            <input
                              type="number"
                              placeholder="±"
                              step="1"
                              value={creditIncrements[user.id] !== undefined ? (creditIncrements[user.id] === 0 ? '' : creditIncrements[user.id]) : ''}
                              onChange={(e) => handleCreditsInputChange(user.id, e.target.value)}
                              onBlur={() => handleCreditsBlur(user.id)}
                              onKeyPress={(e) => handleCreditsKeyPress(e, user.id)}
                              disabled={updatingUser === user.id}
                              className="w-14 text-sm bg-transparent border-0 rounded px-1 py-0.5 text-center focus:outline-none focus:ring-0 tabular-nums placeholder:text-gray-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                            {creditIncrements[user.id] !== undefined && creditIncrements[user.id] > 0 && (
                              <select
                                value={creditChargeTypes[user.id] ?? 'promocional'}
                                onChange={(e) => setCreditChargeTypes(prev => ({
                                  ...prev,
                                  [user.id]: e.target.value as 'promocional' | 'venta'
                                }))}
                                disabled={updatingUser === user.id}
                                className="text-xs bg-transparent border-l border-gray-600 pl-1.5 py-0.5 ml-1 text-gray-300 focus:outline-none focus:ring-0 cursor-pointer min-w-[78px]"
                                title="Tipo de carga"
                              >
                                <option value="promocional">Promocional</option>
                                <option value="venta">Venta</option>
                              </select>
                            )}
                          </div>
                          {creditIncrements[user.id] !== undefined && creditIncrements[user.id] !== 0 && (
                            <>
                              <span className={`text-sm font-medium tabular-nums ${
                                (user.credits + creditIncrements[user.id]) < 0 ? 'text-red-400' : 'text-green-400'
                              }`}>
                                = {user.credits + creditIncrements[user.id]}
                              </span>
                              <button
                                onClick={() => handleSaveCredits(user.id)}
                                disabled={updatingUser === user.id || (user.credits + creditIncrements[user.id]) < 0}
                                className="p-1 rounded bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
                                title="Guardar"
                              >
                                {updatingUser === user.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Check className="w-4 h-4" />
                                )}
                              </button>
                            </>
                          )}
                          {creditIncrements[user.id] !== undefined && (
                            <button
                              onClick={() => {
                                setCreditIncrements(prev => { const s = { ...prev }; delete s[user.id]; return s; });
                                setCreditChargeTypes(prev => { const s = { ...prev }; delete s[user.id]; return s; });
                              }}
                              className="p-1 rounded text-gray-500 hover:text-white hover:bg-gray-700"
                              title="Cancelar"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {formatDateColombia(user.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="inline-flex items-center rounded-full border border-blue-500/20 bg-blue-500/10 px-3 py-1">
                          <span className="text-xs font-medium tracking-wide text-blue-200 font-mono">
                            {formatPhoneForDisplay(user.phone)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-3">
                          <button
                            onClick={() => {
                              setSelectedUserId(user.id);
                              setShowAuditModal(true);
                            }}
                            className="flex items-center text-blue-400 hover:text-blue-300 transition-colors"
                          >
                            <Clock className="w-4 h-4 mr-1" />
                            <span>Historial</span>
                          </button>
                          <button
                            onClick={() => handleDeleteClick(user)}
                            className="text-red-400 hover:text-red-300 transition-colors p-1 rounded-full hover:bg-red-400/10"
                            disabled={loading}
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Paginación */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 bg-[#111827] border-t border-gray-700">
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-400">
                  {filteredUsers.length === 0
                    ? '0 usuarios'
                    : `Mostrando ${startIndex + 1}-${Math.min(startIndex + pageSize, filteredUsers.length)} de ${filteredUsers.length} usuarios`}
                </span>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="text-sm bg-[#1F2937] border border-gray-600 rounded-md px-2 py-1 text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>{size} por página</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                  className="p-2 rounded-lg bg-[#1F2937] border border-gray-600 text-gray-300 hover:bg-[#2D3748] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Página anterior"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="text-sm text-gray-400 min-w-[120px] text-center">
                  Página {currentPage} de {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                  className="p-2 rounded-lg bg-[#1F2937] border border-gray-600 text-gray-300 hover:bg-[#2D3748] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Página siguiente"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Auditoría */}
      {showAuditModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-[#1F2937] rounded-xl shadow-xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto m-4">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold flex items-center">
                <Clock className="w-5 h-5 mr-2" />
                Historial de Cambios
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => selectedUserId && fetchAuditLogs(selectedUserId, true)}
                  className="text-sm text-gray-400 hover:text-white px-2 py-1 rounded hover:bg-gray-700 transition-colors"
                  title="Actualizar"
                >
                  Actualizar
                </button>
                <button
                  onClick={() => setShowAuditModal(false)}
                  className="text-gray-400 hover:text-white p-1 hover:bg-gray-700 rounded-full transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="space-y-4">
              {auditLogs.map((log) => {
                const isCreditChange = log.field_changed === 'credits';
                const chargeTypeMatch = log.new_value?.match(/^(.+?)\s*\((\w+)\)\s*$/);
                const newValueRaw = chargeTypeMatch ? chargeTypeMatch[1].trim() : log.new_value;
                const chargeType = chargeTypeMatch ? chargeTypeMatch[2] : null;

                const oldValue = parseFloat(log.old_value) || 0;
                const newValue = parseFloat(String(newValueRaw)) || 0;
                const increment = newValue - oldValue;

                const formatearDinero = (valor: number) => {
                  return new Intl.NumberFormat('es-CO', {
                    style: 'currency',
                    currency: 'COP',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                  }).format(valor);
                };

                return (
                  <div key={log.id} className="bg-[#111827] rounded-lg p-4 border border-gray-700">
                    <div className="text-sm text-gray-300">
                      <span className="font-medium text-white">
                        {log.modifier_first_name} {log.modifier_last_name}
                      </span>{' '}
                      cambió{' '}
                      <span className="font-medium text-blue-400">
                        {log.field_changed === 'credits' ? 'créditos' : log.field_changed}
                      </span>
                    </div>
                    <div className="mt-2 space-y-2">
                      <div className="flex items-center space-x-3">
                        <div className="flex flex-col">
                          <span className="text-xs text-gray-400 mb-1">Valor anterior</span>
                          <span className="text-gray-300 font-medium">
                            {isCreditChange ? formatearDinero(oldValue) : log.old_value}
                          </span>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-500 mt-5" />
                        <div className="flex flex-col">
                          <span className="text-xs text-gray-400 mb-1">Valor nuevo</span>
                          <span className="text-white font-medium">
                            {isCreditChange ? formatearDinero(newValue) : log.new_value}
                          </span>
                        </div>
                      </div>
                      {isCreditChange && increment !== 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-700">
                          <div className="flex items-center flex-wrap gap-x-4 gap-y-1">
                            <div className="flex items-center space-x-2">
                              <span className="text-xs text-gray-400">
                                {increment > 0 ? 'Recarga:' : 'Retiro:'}
                              </span>
                              <span className={`text-sm font-semibold ${
                                increment > 0 ? 'text-green-400' : 'text-red-400'
                              }`}>
                                {increment > 0 ? '+' : ''}{formatearDinero(increment)}
                              </span>
                            </div>
                            {increment > 0 && chargeType && (
                              <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                                chargeType === 'venta'
                                  ? 'bg-green-900/50 text-green-300'
                                  : 'bg-purple-900/50 text-purple-300'
                              }`}>
                                {chargeType === 'venta' ? 'Por venta' : 'Promocional'}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-3">
                      {formatDateColombia(log.created_at)}
                    </div>
                  </div>
                );
              })}
              {auditLogs.length === 0 && (
                <div className="text-center py-8">
                  <Clock className="w-12 h-12 text-gray-500 mx-auto mb-3" />
                  <p className="text-gray-400">No hay cambios registrados</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Diálogo de confirmación */}
      {showConfirmDialog && userToDelete && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-[#1F2937] rounded-xl shadow-xl p-6 max-w-md w-full m-4">
            <div className="flex items-center space-x-3 text-red-400 mb-4">
              <Trash2 className="w-6 h-6" />
              <h3 className="text-xl font-bold text-white">Confirmar eliminación</h3>
            </div>
            <p className="text-gray-300 mb-6">
              ¿Estás seguro de que deseas eliminar al usuario <span className="font-medium text-white">{userToDelete.first_name} {userToDelete.last_name}</span>? 
              <br /><br />
              <span className="text-red-400">Esta acción eliminará todos sus datos y no se puede deshacer.</span>
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowConfirmDialog(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    <span>Eliminando...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    <span>Eliminar</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 